<template>
  <div class="main-container p-3">
    <KidHeader :profileId="profileId" :serialNumber="serialNumber" />

    <!-- Back -->
    <div class="back-home text-left mb-3">
      <a class="text-black" href="javascript:void(0)" @click="$router.push({ path: '/kids-device', query: { profileId, serialNumber } })">
        <i class="fas fa-arrow-left"></i> <b>Back to Home</b>
      </a>
    </div>

    <!-- Parent Info -->
    <div class="card parent-profile p-3 mb-1">
      <h5>Parent Profile</h5><hr>
      <p><b>Name:</b> {{ parentProfile.name }}</p>
      <p><b>Email:</b> <span class="badge bg-primary text-white">{{ parentProfile.email }}</span></p>
    </div>

    <!-- Security Settings -->
    <div class="card security-card p-3 mb-1">
      <div class="d-flex justify-content-between align-items-center">
        <div class="security-text text-left pr-3">
          <h6 class="mb-1"><i class="fas fa-shield-alt mr-1"></i> Two-Step Email Verification</h6>
          <p class="mb-0 text-muted small">
            When on, we'll email you a verification code each time you log in, for extra account security.
          </p>
        </div>
        <div class="custom-switch-modern">
          <input
            type="checkbox"
            id="twoStepSwitch"
            :checked="twoStepEnabled"
            :disabled="isSecurityUpdating"
            @change="toggleTwoStep($event)"
          >
          <label for="twoStepSwitch"></label>
        </div>
      </div>
    </div>

    <!-- Note Section -->
    <div class="card info-note mb-1" v-if="kidsList.length > 0">
        <p class="mb-0">
            <strong>Note:</strong> Giving Delegated Access means the user can manage your kids on your behalf.
            They may view or control kids' devices depending on the selected access level.
        </p>
    </div>

    <!-- Delegated Access - REDESIGNED -->
    <div class="card p-3 mb-1" v-if="kidsList.length > 0">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0">Delegated Access</h5>
        <button class="btn btn-primary btn-add" @click="showAddModal = true">
          <i class="fas fa-plus"></i> Add Access
        </button>
      </div>

      <!-- Card-based layout -->
      <div v-if="delegatedAccessList.length" class="delegation-cards">
        <div v-for="(item, index) in delegatedAccessList" :key="index" class="delegation-card">
          <!-- User Info Header -->
          <div class="card-header-section">
            <div class="user-info">
              <div class="user-avatar">
                {{ item.name.charAt(0).toUpperCase() }}
              </div>
              <div class="user-details">
                <h6 class="user-name">{{ item.name }}</h6>
                <p class="user-email">{{ item.email }}</p>
              </div>
            </div>
            <span :class="['status-badge', statusBadgeClass(item.status)]">
              {{ item.status }}
            </span>
          </div>

          <!-- Card Body -->
          <div class="card-body-section">
            <div class="info-row">
              <span class="info-label">Access Level</span>
              <span class="info-value">{{ item.accessLevel }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Allowed Kids</span>
              <span class="info-value">{{ item.allowedKids.join(', ') }}</span>
            </div>
          </div>

          <!-- Card Actions -->
          <div class="card-actions">
            <button
              v-if="item.status === 'Pending'"
              class="btn btn-sm btn-outline-primary"
              :disabled="isDisabled"
              @click="resendEmail(item.id)"
            >
              <i class="fas fa-envelope"></i> Resend Email
              <i v-if="isDisabled" class="fa fa-spinner fa-spin ms-1"></i>
            </button>
            <button class="btn btn-sm btn-outline-danger" @click="openConfirmationModal(item.id, 'Revoke')">
              <i class="fas fa-times-circle"></i> Revoke
            </button>
          </div>
        </div>
      </div>

      <!-- No records message -->
      <div v-else class="text-center text-muted py-4">
        <i class="fas fa-users fa-3x mb-3 opacity-25"></i>
        <p>No delegated access found.</p>
      </div>
    </div>

    <!-- Access Received from Others - REDESIGNED -->
    <div class="card p-3 mb-4" v-if="accessReceived.length">
      <h5 class="mb-3">Access Received from Others</h5>
      
      <div class="delegation-cards">
        <div v-for="(item, index) in accessReceived" :key="index" class="delegation-card received">
          <!-- User Info Header -->
          <div class="card-header-section">
            <div class="user-info">
              <div class="user-avatar">
                {{ item.name.charAt(0).toUpperCase() }}
              </div>
              <div class="user-details">
                <h6 class="user-name">{{ item.name }}</h6>
                <p class="user-email">{{ item.email }}</p>
              </div>
            </div>
          </div>

          <!-- Card Body -->
          <div class="card-body-section">
            <div class="info-row">
              <span class="info-label">Access Level</span>
              <span class="info-value">{{ item.accessLevel }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Kids</span>
              <span class="info-value">{{ item.allowedKids.join(', ') }}</span>
            </div>
          </div>

          <!-- Card Actions -->
          <div class="card-actions" v-if="item.status == 'Pending'">
            <button class="btn btn-sm btn-success flex-fill" @click="openConfirmationModal(item.id, 'Accepted')">
              <i class="fas fa-check"></i> Accept
            </button>
            <button class="btn btn-sm btn-danger flex-fill" @click="openConfirmationModal(item.id, 'Rejected')">
              <i class="fas fa-times"></i> Reject
            </button>
          </div>
          <div class="card-actions" v-else>
            <span :class="['status-badge', statusBadgeClass(item.status)]">
              {{ item.status }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- ➕ Modal: Add / Edit Access -->
    <div v-if="showAddModal" class="modal-mask">
      <div class="modal-wrapper">
        <div class="modal-container">
          <h5>{{ isEditing ? 'Edit Delegated Access' : 'Add Delegated Access' }}</h5>

          <div class="form-group text-left">
            <label><b>Name</b></label>
            <input v-model="newDelegate.name" class="form-control" type="text" placeholder="Delegate name" />
          </div>
          <div class="form-group text-left" v-if="parentProfile">
            <label><b>Email</b> <small>(Please enter only your username, not the full email address.)</small></label>
            <div class="input-group">
                <input v-model="newDelegate.email" class="form-control" type="text" placeholder="Delegate email" />
            </div>
          </div>
          <div class="form-group text-left">
            <label><b>Access Level</b></label>
            <select v-model="newDelegate.accessLevel" class="form-control">
              <option value="full">Full</option>
            </select>
          </div>
          <div class="form-group text-left">
            <label><b>Allowed Kids</b></label>
            <div v-for="kid in kidsList" :key="kid._id">
              <input type="checkbox" :id="kid._id" :value="kid._id" v-model="newDelegate.allowedKids" />
              <label :for="kid.id">&nbsp;{{ kid.profile_name }}</label>
            </div>
          </div>
          <div class="text-right mt-3">
            <button class="btn btn-secondary mr-2" @click="closeModal">Cancel</button>
            <button class="btn btn-success" @click="saveDelegatedAccess">
              {{ isEditing ? 'Update' : 'Add' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm Modal -->
    <div class="modal fade" id="confirmAccessModal" tabindex="-1" role="dialog" aria-labelledby="confirmAccessModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="confirmAccessModalLabel">Confirm Action</h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            Are you sure you want to <strong>{{ pendingAction.status }}</strong> this delegate access request?
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-primary" @click="confirmDelegateAccess">Yes, Confirm</button>
          </div>
        </div>
      </div>
    </div>

    <KidFooter :profileId="profileId" :serialNumber="serialNumber" />
  </div>
</template>
<script>
import KidHeader from '@/components/KidHeader.vue'
import KidFooter from '@/components/KidFooter.vue'
import UserService from "@/services/UserService";

export default {
  name: 'DelegatedAccessPage',
  components: {
    KidHeader,
    KidFooter,
  },
  data() {
  return {
    profileId: this.$route.query.profileId,
    serialNumber: this.$route.query.serialNumber,

    isDisabled: false,
    parentProfile: {
      name: "Loading...",
      email: "Loading..."
    },

    delegatedAccessList: [],
    kidsList: [],
    accessReceived: [],

    showAddModal: false,
    isEditing: false,
    editingIndex: null,

    newDelegate: {
      name: '',
      email: '',
      accessLevel: 'full',
      allowedKids: []
    },
    pendingAction: {
      id: null,
      status: ''
    },
    twoStepEnabled: false,
    isSecurityUpdating: false,
  };
},
methods: {
  async fetchParentProfile() {
    await UserService.fetchParentProfile()
        .then((response) => {
          this.parentProfile = response.data.data;
          this.twoStepEnabled = !!response.data.data.enable_two_step_email_verification;  // NEW
          this.kidsList = response.data.kidsList
          this.delegatedAccessList = response.data.delegatedAccessList
          this.accessReceived = response.data.accessReceivedList
        }).catch((e) => {
          this.$toast.error(e.response.data.respones_msg);
        });
  },
  closeModal() {
    this.showAddModal = false;
    this.isEditing = false;
    this.newDelegate = { name: '', email: '', accessLevel: 'view', allowedKids: [] };
  },
  resendEmail(id) {
    this.isDisabled = true
    UserService.resendDelegatedAccessEmail({ id: id })
        .then((response) => {
          this.isDisabled = false
          this.$toast.success(response.data.respones_msg);
        }).catch((e) => {
          this.isDisabled = false
          this.$toast.error(e.response.data.respones_msg);
        });
  },
  saveDelegatedAccess() {
    if (!this.newDelegate.email || !this.newDelegate.name || !this.newDelegate.allowedKids.length) {
      this.$toast.error("Please fill all required fields.");
      return;
    }
    let data = { ...this.newDelegate }; // spread operator to clone
    //data.email = data.email + "@" + this.parentProfile.email.split('@')[1];
    
    UserService.saveDelegatedAccess(data)
        .then((response) => {
          this.$toast.success(response.data.respones_msg);
          this.fetchParentProfile();
          this.closeModal();     
        }).catch((e) => {
          this.$toast.error(e.response.data.respones_msg);
        });
  },
  editAccess(index) {
    const access = this.delegatedAccessList[index];
    this.newDelegate = { ...access };
    this.editingIndex = index;
    this.isEditing = true;
    this.showAddModal = true;
  },
  revokeAccess(index) {
    
    this.$toast.success("Access revoked.");
  },
  openConfirmationModal(id, status) {
    this.pendingAction.id = id;
    this.pendingAction.status = status;
    $('#confirmAccessModal').modal('show');
  },
  
  confirmDelegateAccess() {
    const { id, status } = this.pendingAction;

    UserService.updateDelegateAccessStatus({ id, status })
      .then((response) => {
        this.$toast.success(response.data.respones_msg);
        if (status == "Accepted") {
          this.$router.push({ path: '/kids-device'})
        }
        this.fetchParentProfile();
      })
      .catch((e) => {
        this.$toast.error(e.response.data.respones_msg);
      })
      .finally(() => {
        // Hide the modal
        $('#confirmAccessModal').modal('hide');
      });
  },

  statusBadgeClass(status) {
    switch (status) {
      case 'Accepted': return 'bg-success text-white';
      case 'Pending': return 'bg-warning text-dark';
      case 'Rejected': return 'bg-danger text-white';
      default: return 'bg-secondary text-white';
    }
  },
  async toggleTwoStep(event) {
    if (this.isSecurityUpdating) return;

    const newValue = event.target.checked;
    const oldValue = this.twoStepEnabled;

    this.twoStepEnabled = newValue;      // optimistic update
    this.isSecurityUpdating = true;

    try {
      const response = await UserService.updateSecuritySetting({
        enable_two_step_email_verification: newValue
      });
      this.parentProfile.enable_two_step_email_verification = newValue;
      this.$toast.success(
        response.data.respones_msg ||
        `Two-step verification ${newValue ? 'enabled' : 'disabled'}`
      );
    } catch (e) {
      // Revert both the data and the DOM checkbox
      this.twoStepEnabled = oldValue;
      event.target.checked = oldValue;
      this.$toast.error(
        e.response?.data?.respones_msg || 'Failed to update security setting'
      );
    } finally {
      this.isSecurityUpdating = false;
    }
  },
},
watch: {
  kidsList: {
    immediate: true,
    handler(newList) {
      if (Array.isArray(newList)) {
        this.newDelegate.allowedKids = newList.map(kid => kid._id);
      }
    }
  }
},
mounted() {
  this.fetchParentProfile();
}

}
</script>

<style scoped>
.card {
  border-radius: 10px;
  background: #fff;
  padding: 20px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  width: 100% !important;
}

.parent-profile {
  width: 100%;
  margin: 0 0 20px 0;
  padding: 20px;
  background: #fff;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  border-radius: 10px;
}

.back-home {
  display: block;
  width: 100%;
  margin-top: 10px;
  margin-bottom: 10px;
  font-size: 1rem;
  z-index: 1;
  position: relative;
}

.badge {
  padding: 5px 10px;
  font-size: 0.9rem;
  border-radius: 5px;
}

.info-note {
  background: #e9f7fe;
  border: 1px solid #bce8f1;
  color: #31708f;
  padding: 15px;
  border-radius: 10px;
  font-size: 12px;
}

/* ===== NEW CARD-BASED DESIGN ===== */

.btn-add {
  font-size: 0.9rem;
  padding: 8px 16px;
  border-radius: 8px;
}

.delegation-cards {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.delegation-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

.delegation-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  transform: translateY(-2px);
}

.delegation-card.received {
  border-left: 4px solid #007bff;
}

/* Card Header */
.card-header-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
  flex-shrink: 0;
}

.user-details {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.user-name {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
}

.user-email {
  margin: 2px 0 0 0;
  font-size: 0.85rem;
  color: #718096;
  word-break: break-all;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.status-badge.bg-success {
  background-color: #48bb78 !important;
  color: white !important;
}

.status-badge.bg-warning {
  background-color: #ecc94b !important;
  color: #2d3748 !important;
}

.status-badge.bg-danger {
  background-color: #f56565 !important;
  color: white !important;
}

.status-badge.bg-secondary {
  background-color: #a0aec0 !important;
  color: white !important;
}

/* Card Body */
.card-body-section {
  margin-bottom: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  gap: 12px;
}

.info-label {
  font-size: 0.85rem;
  color: #718096;
  font-weight: 500;
  flex-shrink: 0;
}

.info-value {
  font-size: 0.9rem;
  color: #2d3748;
  font-weight: 500;
  text-align: right;
  word-break: break-word;
}

/* Card Actions */
.card-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.card-actions .btn {
  flex: 1;
  font-size: 0.85rem;
  padding: 8px 12px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.card-actions .btn i {
  font-size: 0.9rem;
}

.card-actions .btn-outline-primary:hover {
  background-color: #007bff;
  color: white;
}

.card-actions .btn-outline-danger:hover {
  background-color: #dc3545;
  color: white;
}

/* Modal Styles */
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
}

.modal-wrapper {
  width: 100%;
  max-width: 500px;
}

.modal-container {
  background: white;
  padding: 20px;
  border-radius: 10px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* Responsive Design */
@media (max-width: 576px) {
  .card-header-section {
    flex-direction: column;
    gap: 12px;
  }

  .status-badge {
    align-self: flex-start;
  }

  .user-avatar {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }

  .user-name {
    font-size: 0.95rem;
  }

  .user-email {
    font-size: 0.8rem;
  }

  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .info-value {
    text-align: left;
  }

  .card-actions {
    flex-direction: column;
  }

  .card-actions .btn {
    width: 100%;
  }

  .modal-container {
    padding: 15px;
  }

  .btn-add {
    font-size: 0.85rem;
    padding: 6px 12px;
  }

  .btn-add i {
    display: none;
  }
}

@media (min-width: 768px) {
  .delegation-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 20px;
  }
}

/* Security card */
.security-card h6 {
  font-weight: 700;
  color: #1f2937;
}

.security-card h6 i {
  color: #6366f1;
}

/* Toggle switch */
.custom-switch-modern {
  flex-shrink: 0;
}

.custom-switch-modern input[type="checkbox"] {
  display: none;
}

.custom-switch-modern label {
  width: 52px;
  height: 28px;
  background: #d1d5db;
  border-radius: 50px;
  position: relative;
  cursor: pointer;
  transition: background 0.3s ease;
  display: block;
  margin: 0;
}

.custom-switch-modern label:after {
  content: '';
  width: 22px;
  height: 22px;
  background: #fff;
  border-radius: 50%;
  position: absolute;
  top: 3px;
  left: 3px;
  transition: left 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.custom-switch-modern input:checked + label {
  background: #10b981;
}

.custom-switch-modern input:checked + label:after {
  left: 27px;
}

.custom-switch-modern input:disabled + label {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>